import { Box, CircularProgress, IconButton, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Star1, TickCircle } from "iconsax-react";
import { useTranslation } from "react-i18next";

import { FloatingTextField, SefanoButton } from "@/components/Molecules";
import type { SettingsSocialsProps } from "@/components/Templates/Settings/Settings.template.types";
import * as styles from "./SettingsSocialsPanel.styles";

type SettingsSocialsPanelProps = SettingsSocialsProps;

export function SettingsSocialsPanel({
  rows,
  onSubmit,
  isLoading,
  isSubmitting,
  saved,
  saveError,
}: SettingsSocialsPanelProps) {
  const { t } = useTranslation("settings");
  const theme = useTheme();

  if (isLoading) {
    return <CircularProgress size={32} />;
  }

  return (
    <Box sx={styles.stack}>
      <Box sx={styles.banner}>
        <Star1 size={19} variant="Bold" color={theme.palette.secondary.main} />
        <Typography sx={{ fontSize: { xs: "0.65625rem", md: "0.75rem" }, color: "text.secondary", lineHeight: 1.7 }}>
          <Box component="span" sx={{ display: { xs: "inline", md: "none" } }}>
            {t("socials.mobileBanner")}
          </Box>
          <Box component="span" sx={{ display: { xs: "none", md: "inline" } }}>
            {t("socials.banner")}
          </Box>
        </Typography>
      </Box>

      {rows.map((row) => {
        const PlatformIcon = row.icon;
        return (
          <Box key={row.key} sx={styles.socialRow(row.isPrimary)}>
            <Box sx={styles.platformIcon(row.brandColor)}>
              <PlatformIcon size={20} variant="Bold" color={row.brandColor} />
            </Box>
            <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 0.5 }}>
              <FloatingTextField
                label={t(row.label)}
                dir="ltr"
                placeholder={t(row.placeholder)}
                value={row.value}
                onChange={(event) => row.onChange(event.target.value)}
              />
              <Typography sx={{ fontSize: { xs: "0.625rem", md: "0.6875rem" }, color: "text.secondary", px: 0.25 }}>
                {t(row.helper)}
              </Typography>
            </Box>
            <IconButton
              size="small"
              onClick={row.onSetPrimary}
              title={
                row.isFilled
                  ? row.isPrimary
                    ? t("socials.primaryTitle")
                    : t("socials.setPrimaryTitle")
                  : t("socials.fillFirst")
              }
              sx={styles.starButton(row.isFilled, row.isPrimary)}
            >
              <Star1
                size={20}
                variant={row.isPrimary ? "Bold" : "Linear"}
                color={row.isPrimary ? theme.palette.secondary.main : theme.palette.text.secondary}
              />
            </IconButton>
          </Box>
        );
      })}

      {saveError ? (
        <Typography role="alert" sx={{ fontSize: "0.75rem", fontWeight: 600, color: "error.main" }}>
          {saveError}
        </Typography>
      ) : null}

      <Box sx={styles.footerRow}>
        <SefanoButton disabled={isSubmitting} onClick={onSubmit} sx={{ width: { xs: "100%", md: 150 } }}>
          {isSubmitting ? t("saving") : t("socials.save")}
        </SefanoButton>
        {saved ? (
          <Box component="span" sx={styles.savedFlash}>
            <TickCircle size={16} variant="Bold" color={theme.palette.success.main} />
            {t("saved")}
          </Box>
        ) : null}
      </Box>
    </Box>
  );
}
